import { useMemo } from "react";

import { useSwrFetch } from "./useSwrFetch";

import type {
  CarrierMetadata,
  CarrierStatsResponse,
} from "types/company/carrier";

interface Props {
  companyId: number | null;
}

function toCarrierMetadata(
  carrierStats: CarrierStatsResponse,
): CarrierMetadata {
  return {
    subscriptionPlan: carrierStats.subscription_plan,
    totalShipmentsBooked: carrierStats.total_shipments_booked,
    totalQuotesCreated: carrierStats.total_quotes_created,
    currentShipperNetworkSize: carrierStats.current_shipper_network_size,
    averageReviewRating: carrierStats.reviews_meta,
    microReviewRating: {
      rating: carrierStats.micro_reviews_rating.rating,
      microReviewsCount: carrierStats.micro_reviews_rating.micro_reviews_count,
    },
    // Normalized at the boundary so `CarrierMetadata` matches its declared
    // type: a backend older than this field omits the key rather than sending
    // null.
    trackingLinkSubmissionRate:
      carrierStats.tracking_link_submission_rate ?? null,
  };
}

export function useCarrierStats(props: Props) {
  const { companyId } = props;

  const url = useMemo(
    () => (companyId !== null ? `/companies/${companyId}/carrier-stats` : null),
    [companyId],
  );

  const {
    data: carrierStats,
    loading,
    error,
    refetch: refetchStats,
  } = useSwrFetch<CarrierStatsResponse>({
    url,
    errorMessage: "Failed to fetch carrier stats",
  });

  const carrierMeta = useMemo(
    () => (carrierStats ? toCarrierMetadata(carrierStats) : null),
    [carrierStats],
  );

  return {
    carrierMeta,
    loading,
    error,
    refetchStats,
  };
}
